'use strict';

const BaseScene = require('./base-scene');
const Button = require('../ui/button');
const Player = require('../entities/player');
const BulletPool = require('../gameplay/bullet-pool');
const PatternEmitter = require('../gameplay/patterns');
const { circlesOverlap } = require('../gameplay/collision');
const { getPlayerScale } = require('../config/levels');
const { COLORS, GAME_WIDTH, GAME_HEIGHT } = require('../config/constants');
const { drawPanel, drawText, drawGlowText } = require('../ui/draw');

class GameScene extends BaseScene {
  enter(payload) {
    this.level = Math.max(1, Math.min(99, Number(payload.level) || 1));
    this.runContext = {
      ranked: Boolean(payload.ranked),
      runId: payload.runId || null,
      configVersionId: payload.configVersionId || null,
      startedAt: payload.startedAt || null,
      level: this.level
    };
    this.config = payload.config || this.app.gameConfig.getLevelConfig(this.level);
    this.app.bossAssets.preloadAnimal(this.level);
    this.remaining = this.config.duration;
    this.elapsed = 0;
    this.finished = false;
    this.handedOff = false;
    this.paused = false;
    this.dragging = false;
    this.forceResumePrompt = false;
    this.pauseCount = 0;
    this.pauseDurationMs = 0;
    this.pauseStartedAt = null;
    this.flash = 0;
    this.bounds = {
      left: 0,
      right: GAME_WIDTH,
      top: Math.max(142, this.app.renderer.safeTop + 110),
      bottom: Math.min(GAME_HEIGHT, this.app.renderer.safeBottom)
    };
    const startX = (this.bounds.left + this.bounds.right) / 2;
    const startY = (this.bounds.top + this.bounds.bottom) / 2;
    this.player = new Player(startX, startY, {
      planeColor: this.app.cosmeticColor('PLANE', COLORS.cyan),
      trailColor: this.app.cosmeticColor('TRAIL', COLORS.cyan),
      level: this.level,
      scale: getPlayerScale(this.level)
    });
    this.pool = new BulletPool(200);
    this.pool.setLimit(this.config.maxBullets);
    this.emitter = new PatternEmitter(this.config, this.bounds);
    this.coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    this.showTutorial = this.level === 1 && (
      this.coarsePointer
        ? !this.app.storage.hasSeenTouchpadTutorial()
        : !this.app.storage.hasSeenTutorial()
    );
    this.pauseButton = new Button({
      x: 632,
      y: Math.max(36, this.app.renderer.safeTop + 10),
      width: 82,
      height: 68,
      label: 'Ⅱ',
      variant: 'secondary',
      fontSize: 29,
      onTap: () => this.setPaused(true)
    });
    this.continueButton = new Button({
      x: 205,
      y: 710,
      width: 340,
      height: 92,
      label: '继续游戏',
      onTap: () => this.setPaused(false)
    });
  }

  exit() {
    this.dragging = false;
    this.app.input.cancelPointer();
    if (!this.finished && !this.handedOff) this.app.abandonRun(this.runContext);
    if (this.pool) this.pool.clear();
    super.exit();
  }

  setPaused(paused) {
    const next = Boolean(paused);
    if (next === this.paused) return;
    this.paused = next;
    if (next) {
      this.pauseCount += 1;
      this.pauseStartedAt = performance.now();
      this.dragging = false;
      this.app.input.cancelPointer();
    } else {
      if (this.pauseStartedAt !== null) {
        this.pauseDurationMs += Math.max(0, performance.now() - this.pauseStartedAt);
      }
      this.pauseStartedAt = null;
      this.forceResumePrompt = false;
    }
    this.app.feedback.setMusicPaused(next);
  }

  onAppHide() {
    if (!this.finished) {
      this.setPaused(true);
      this.forceResumePrompt = true;
      this.dragging = false;
      this.app.input.cancelPointer();
    }
  }

  onAppShow() {
    if (this.forceResumePrompt) this.paused = true;
  }

  handleTouch(type, point, gesture) {
    if (this.finished) return;
    if (this.paused) {
      if (type === 'start' && this.continueButton.contains(point.x, point.y)) {
        this.app.feedback.tap();
        this.continueButton.tap();
        this.app.input.cancelPointer();
      }
      return;
    }
    if (type === 'start' && this.pauseButton.contains(point.x, point.y)) {
      this.app.feedback.tap();
      this.pauseButton.tap();
      return;
    }
    if (this.coarsePointer) {
      const relative = gesture || { dx: 0, dy: 0 };
      if (type === 'move' && (relative.dx || relative.dy)) {
        if (this.showTutorial) {
          this.showTutorial = false;
          this.app.storage.markTouchpadTutorialSeen();
        }
        const delta = this.app.renderer.clientDeltaToLogical(relative.dx, relative.dy);
        const sensitivity = this.app.gameConfig.getTouchSensitivity(
          this.app.settings.touchSensitivity
        );
        this.player.moveBy(delta.x * sensitivity, delta.y * sensitivity, this.bounds);
      }
      return;
    }
    if (this.showTutorial && type === 'start') {
      this.showTutorial = false;
      this.app.storage.markTutorialSeen();
    }
    if (type === 'start' && this.player.contains(point.x, point.y)) {
      this.dragging = true;
      this.player.setTarget(point.x, point.y);
    } else if (type === 'move' && this.dragging) {
      this.player.setTarget(point.x, point.y);
    } else if (type === 'end' || type === 'cancel') {
      this.dragging = false;
    }
  }

  handleKeyboard(dt, vector) {
    if (this.finished || this.paused || (!vector.x && !vector.y)) return;
    if (this.showTutorial) {
      this.showTutorial = false;
      this.app.storage.markTutorialSeen();
    }
    this.player.moveBy(vector.x * 320 * dt, vector.y * 320 * dt, this.bounds);
  }

  update(dt) {
    if (this.paused || this.finished || this.showTutorial) return;
    this.elapsed += dt;
    this.remaining = Math.max(0, this.config.duration - this.elapsed);
    this.player.update(dt, this.bounds);
    this.emitter.update(dt, this.player, this.pool);
    this.pool.update(dt, this.bounds, this.player);
    const playerCircle = this.player.getHitCircle();
    const bullets = this.pool.getActive();
    for (let i = 0; i < bullets.length; i += 1) {
      if (circlesOverlap(playerCircle, bullets[i].getHitCircle())) {
        this.finish(false);
        return;
      }
    }
    if (this.remaining <= 0) this.handoffToBoss();
    if (this.flash > 0) this.flash = Math.max(0, this.flash - dt);
  }

  finish(success) {
    if (this.finished) return;
    this.finished = true;
    this.flash = 0.18;
    this.dragging = false;
    this.app.input.cancelPointer();
    const activeDurationMs = Math.round(Math.min(this.elapsed, this.config.duration) * 1000);
    const settlementPromise = this.app.completeRun(this.runContext, {
      cleared: Boolean(success),
      activeDurationMs,
      pauseCount: this.pauseCount,
      pauseDurationMs: Math.round(this.pauseDurationMs)
    });
    if (success) this.app.feedback.success();
    else this.app.feedback.hit();
    this.app.switchScene('result', {
      success,
      level: this.level,
      elapsed: Math.min(this.elapsed, this.config.duration),
      ranked: this.runContext.ranked,
      settlementPromise
    });
  }

  handoffToBoss() {
    if (this.finished || this.handedOff) return;
    this.handedOff = true;
    this.dragging = false;
    this.app.input.cancelPointer();
    const activeDurationMs = Math.round(Math.min(this.elapsed, this.config.duration) * 1000);
    this.pool.clear();
    this.app.performSwitch('boss', {
      runContext: this.runContext,
      config: this.config,
      survivalDurationMs: activeDurationMs,
      pauseCount: this.pauseCount,
      pauseDurationMs: Math.round(this.pauseDurationMs)
    });
  }

  render(ctx) {
    this.drawPlayArea(ctx);
    this.pool.draw(ctx);
    this.player.draw(ctx);
    this.drawHud(ctx);
    this.pauseButton.draw(ctx);
    if (this.showTutorial) this.drawTutorial(ctx);
    if (this.paused) this.drawPause(ctx);
  }

  drawPlayArea(ctx) {
    ctx.save();
    const gradient = ctx.createLinearGradient(0, this.bounds.top, 0, this.bounds.bottom);
    gradient.addColorStop(0, 'rgba(28, 65, 130, 0.1)');
    gradient.addColorStop(1, 'rgba(5, 13, 35, 0.22)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, this.bounds.top, GAME_WIDTH, this.bounds.bottom - this.bounds.top);
    ctx.strokeStyle = 'rgba(70, 230, 255, 0.16)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, this.bounds.top);
    ctx.lineTo(GAME_WIDTH, this.bounds.top);
    ctx.stroke();
    ctx.restore();
  }

  drawHud(ctx) {
    const top = Math.max(32, this.app.renderer.safeTop + 8);
    drawPanel(ctx, 28, top, 582, 72, {
      radius: 20,
      fill: 'rgba(5, 17, 43, 0.82)',
      stroke: 'rgba(70, 230, 255, 0.3)'
    });
    drawText(ctx, `第 ${this.level} 关`, 112, top + 36, 28, COLORS.text, 'center', 'bold');
    drawText(ctx, '生存', 265, top + 36, 23, COLORS.muted);
    drawText(ctx, `${this.remaining.toFixed(1)}s`, 365, top + 36, 31, COLORS.success, 'center', 'bold');
    drawText(ctx, `${this.pool.activeCount()} 发`, 520, top + 36, 23, COLORS.muted);
  }

  drawTutorial(ctx) {
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.58)';
    ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    drawPanel(ctx, 90, 470, 570, 310, {
      fill: 'rgba(8, 24, 58, 0.96)',
      stroke: COLORS.cyan
    });
    drawGlowText(ctx, this.coarsePointer ? '全屏触控' : '拖动飞机', GAME_WIDTH / 2, 555, 44, COLORS.cyan);
    drawText(ctx, this.coarsePointer ? '在画面任意位置滑动' : '按住飞机并拖动', GAME_WIDTH / 2, 635, 30, COLORS.text);
    drawText(ctx, '避开所有移动的子弹', GAME_WIDTH / 2, 685, 30, COLORS.text);
    drawText(ctx, this.coarsePointer ? '滑动屏幕开始计时' : '触摸屏幕开始计时', GAME_WIDTH / 2, 748, 24, COLORS.warning);
    ctx.restore();
  }

  drawPause(ctx) {
    ctx.save();
    ctx.fillStyle = 'rgba(1, 5, 16, 0.78)';
    ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    drawGlowText(ctx, '游戏暂停', GAME_WIDTH / 2, 570, 56, COLORS.cyan);
    drawText(ctx, '准备好后继续', GAME_WIDTH / 2, 645, 27, COLORS.muted);
    this.continueButton.draw(ctx);
    ctx.restore();
  }
}

module.exports = GameScene;
